import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * useContinuousWeeks — 월간 뷰 "끊기지 않는 연속 스크롤"의 창(window) 관리.
 *
 * 고정 개수(월 단위가 아니라 "지금 마운트된 주(week) 개수")만 항상 DOM 에 유지한다.
 * 스크롤이 위/아래 가장자리 근처(EDGE_PREFETCH_PX)에 닿으면 그쪽으로 EXTEND_WEEKS 만큼
 * 더 붙이고, 총 개수가 MAX_WEEKS 를 넘으면 반대쪽(화면 밖) 끝에서 잘라낸다 — react-window 같은
 * 가상화 라이브러리 없이(신규 의존성 추가 없음), 주 row 높이가 고정값(WEEK_ROW_HEIGHT_PX)이라
 * "몇 번째 주가 화면 상단에 걸려 있는가" 계산이 정확하다는 점을 이용한 손수 windowing.
 *
 * ⚠ 위쪽(과거)에 주를 추가/제거하면 그 아래 있던 화면이 그만큼 밀려 "점프"해 보인다.
 *   useLayoutEffect(페인트 전에 동기 실행)로 scrollTop 을 같은 양만큼 보정해 시각적으로는
 *   가만히 있는 것처럼 만든다 — 채팅 기록(useAutoScroll.ts)이 겪었던 "수동 scrollTop 보정"
 *   문제와 같은 종류지만, 여기는 row 높이가 고정이라 보정량이 정확히 계산되고, 방향도 항상
 *   "지금 스크롤 중인 반대쪽 끝만 자른다"로 못박아 둬서 그 케이스보다 단순하다.
 */

// 2026-08-21 — 사용자 요청으로 기존(160px) 대비 축소, 세로로 너무 길다는 피드백(120px 로 확정).
// CalendarPage.css 의 .calendar-month-week·CalendarShell.css 의 셸 오버라이드 둘 다 반드시
// 같은 값으로 맞춘다(스크롤 위치→주 인덱스 계산의 전제 — 어긋나면 3-9 에서 겪은 것과 같은
// CSS 캐스케이드 버그가 재발한다).
export const WEEK_ROW_HEIGHT_PX = 120;
const INITIAL_PAST_WEEKS = 6;
const INITIAL_FUTURE_WEEKS = 6; // 초기 13주(오늘이 속한 주 ± 6주) 렌더.
const EXTEND_WEEKS = 6;
const MAX_WEEKS = 24; // 5~6개월치. 넘으면 반대쪽(화면 밖) 끝을 잘라 DOM 노드 수를 억제.
const EDGE_PREFETCH_PX = 2 * WEEK_ROW_HEIGHT_PX; // 가장자리에서 1~2주 남았을 때 미리 확장(끊김 없이).

/** 로컬 자정, 일요일 기준 그 주의 시작일. */
export function weekStartOf(d: Date): Date {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  s.setDate(s.getDate() - s.getDay());
  return s;
}
function addWeeks(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n * 7);
  return r;
}
function sameWeek(a: Date, b: Date): boolean {
  return a.getTime() === b.getTime();
}
/** 어떤 주의 시작일 → 그 주가 끝나는(=다음 주가 시작하는) 시각. onNeedRange 의 "to" 는
 *  항상 이걸로 넘겨야 마지막 주의 7일이 통째로 fetch 범위에 들어간다(시작일만 넘기면 그
 *  주 자체가 빠지는 off-by-one이 된다). */
function weekEnd(weekStart: Date): Date {
  return addWeeks(weekStart, 1);
}
/** 그 주(7일)에서 가장 많은 날을 차지하는 달 — 월 경계에 걸친 주(예: 1/28~2/3)를 어느 달
 *  행으로 셀지 판정한다. 7일이 두 달에 나뉘면 항상 한쪽이 4일 이상이라 동률은 없다. */
function dominantMonthOfWeek(weekStart: Date): { year: number; month: number } {
  const counts = new Map<string, number>();
  let best = { year: weekStart.getFullYear(), month: weekStart.getMonth() };
  let bestCount = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const n = (counts.get(key) ?? 0) + 1;
    counts.set(key, n);
    if (n > bestCount) { bestCount = n; best = { year: d.getFullYear(), month: d.getMonth() }; }
  }
  return best;
}
function buildWindow(center: Date, futureWeeks: number = INITIAL_FUTURE_WEEKS): Date[] {
  const c = weekStartOf(center);
  const out: Date[] = [];
  for (let i = -INITIAL_PAST_WEEKS; i <= futureWeeks; i++) out.push(addWeeks(c, i));
  return out;
}

interface UseContinuousWeeksArgs {
  containerRef: RefObject<HTMLDivElement>;
  initialDate: Date;
  /** 마운트된 주 범위가 바뀔 때마다(확장·점프) 그 전체 구간을 알려준다 — 호출부가 이벤트를 채워 넣는다. */
  onNeedRange: (from: Date, to: Date) => void;
  /** 화면 상단에 걸린 주가 바뀔 때 — 헤더 타이틀 동기화용. */
  onVisibleMonthChange?: (year: number, month: number) => void;
  /**
   * ⭐ 2026-10-19 — 주 칸 높이(px). 안 주면 WEEK_ROW_HEIGHT_PX(120) — 종전 그대로.
   *   바뀌면 «화면 맨 위에 걸린 주»가 그대로 남도록 scrollTop 을 같은 비율로 옮긴다.
   */
  rowHeight?: number;
}

export function useContinuousWeeks({ containerRef, initialDate, onNeedRange, onVisibleMonthChange, rowHeight }: UseContinuousWeeksArgs) {
  const rowH = rowHeight ?? WEEK_ROW_HEIGHT_PX;
  /** 셈에 쓰는 주 칸 높이 — 아래 효과가 바뀐 값으로 넘겨받는다(그 전까지는 옛 높이로 센다). */
  const rowHRef = useRef(rowH);
  /**
   * 높이가 바뀌기 «직전» 위치(몇 번째 주 + 비율). 그리기 단계에서 옛 DOM 으로 읽어 둔다 —
   * 효과 단계에서는 칸이 이미 새 높이라 scrollTop 이 그 사이 달라져 있을 수 있다(실측: 한 주 밀렸다).
   */
  const anchorRowsRef = useRef<number | null>(null);
  if (rowH !== rowHRef.current && anchorRowsRef.current == null && containerRef.current) {
    anchorRowsRef.current = containerRef.current.scrollTop / rowHRef.current;
  }
  const [weeks, setWeeks] = useState<Date[]>(() => buildWindow(initialDate));

  const extendingRef = useRef(false);
  const pendingScrollDeltaRef = useRef(0);
  const pendingScrollTopRef = useRef<number | null>(INITIAL_PAST_WEEKS * rowHRef.current);
  const mountedRef = useRef(false);
  const lastVisibleMonthRef = useRef('');
  // 아래 [weeks] 보정 effect 가 el.scrollTop 을 직접 쓰면 브라우저가 비동기로 새 'scroll'
  // 이벤트를 또 낸다(속성 대입 ≠ 동기 이벤트). 그 메아리를 그대로 onScroll 이 받으면 방금
  // 보정한 위치가 여전히 가장자리 근처라 extendPast/Future 가 다시 발동 — 이게 반복되며
  // window 가 한 프레임 안에 여러 번 밀리다 못해 화면이 완전히 다른 범위로 튀어 보였다
  // (2026-08-24, 실사용자 재현: scrollTop 을 가장자리 근처로 직접 옮기면 즉시 몇 단계가
  // 연쇄 발동해 요청한 위치와 전혀 다른 곳에 멈췄다). 값 비교로 메아리를 가른다(단순 플래그가
  // 아닌 이유: 보정 직후 실제 사용자가 또 스크롤하면 그 이벤트까지 삼켜선 안 된다) — 우리가
  // 방금 쓴 값을 기억해 뒀다가, onScroll 이 읽은 scrollTop 이 정확히 그 값과 같을 때만
  // "우리가 낸 메아리"로 보고 건너뛴다. 그 사이 값이 달라졌으면(사용자가 더 스크롤) 정상 처리.
  const lastSelfScrollTopRef = useRef<number | null>(null);
  // 2026-08-24 — 메아리를 걸러내도 여전히 남는 문제 하나 더: 보정 폭(EXTEND_WEEKS*ROW_HEIGHT
  // = 6*120 = 720px)이 가장자리 판정 문턱(EDGE_PREFETCH_PX = 240px)보다 3배 크다. 그래서
  // "아래로 스크롤 → extendFuture → 보정으로 scrollTop 720px 감소" 가 그 자체로 "위쪽
  // 가장자리(240px 미만)" 안에 착지해 버릴 수 있고, 그러면 바로 다음 평가에서 extendPast 가
  // 불려 방금 붙인 미래 주를 도로 잘라내며 뒤로 밀린다 — 사용자가 계속 아래로 스크롤 중인데도
  // 화면은 몇 달 전으로 튀어 보였다("7월을 기대했는데 4월이 보인다"). 위치만으로 판단하지
  // 않고 방향을 함께 본다 — 실제로 아래로 스크롤 중이면 extendPast 를, 위로 스크롤 중이면
  // extendFuture 를 절대 부르지 않는다(첫 평가처럼 방향을 모르면 기존처럼 둘 다 허용).
  const prevScrollTopRef = useRef<number | null>(null);

  // 최초 마운트 — 초기 창의 이벤트 구간을 요청 + 헤더 동기화(스크롤 없이도 바로 맞는 값).
  useLayoutEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    onNeedRange(weeks[0]!, weekEnd(weeks[weeks.length - 1]!));
    // 2026-09-03 — "새로고침하면 지난달이 뜬다" 리포트. 오늘이 속한 주가 지난달에서 시작하면
    // (예: 9/3은 8/30(일)~9/5 주에 속함) 주 시작일의 달을 그대로 읽어 "8월"이 잘못 뜨는
    // 버그였다. notifyVisibleMonth/scrollToDate 와 동일하게 그 주 안에서 더 많은 날을
    // 차지하는 달을 고른다(dominantMonthOfWeek) — 둘은 이미 이렇게 하고 있었는데 최초
    // 마운트 시점 동기화만 빠져 있었다.
    const w = weeks[INITIAL_PAST_WEEKS]!;
    const dom = dominantMonthOfWeek(w);
    onVisibleMonthChange?.(dom.year, dom.month);
    lastVisibleMonthRef.current = `${dom.year}-${dom.month}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // weeks 가 바뀔 때마다(확장·트리밍·점프) 시각적 점프를 보정 — 페인트 전에 동기 실행.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el && (pendingScrollTopRef.current != null || pendingScrollDeltaRef.current !== 0)) {
      if (pendingScrollTopRef.current != null) {
        el.scrollTop = pendingScrollTopRef.current;
        pendingScrollTopRef.current = null;
        prevScrollTopRef.current = null; // 불연속 점프(scrollToDate) — 방향 추적 리셋.
      } else {
        el.scrollTop += pendingScrollDeltaRef.current;
        // 방향 기준점도 같은 폭만큼 옮겨야 "보정 직후 다음 실제 스크롤"의 방향 판정이
        // 안 어긋난다(안 옮기면 보정 폭만큼 방향이 뒤집힌 것처럼 오판될 수 있다).
        if (prevScrollTopRef.current != null) prevScrollTopRef.current += pendingScrollDeltaRef.current;
        pendingScrollDeltaRef.current = 0;
      }
      // 방금 쓴 실제 값(클램프됐을 수 있음) — onScroll 이 이 값과 같은 걸 받으면 메아리로 본다.
      lastSelfScrollTopRef.current = el.scrollTop;
    }
    extendingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks]);

  function extendPast() {
    if (extendingRef.current) return;
    extendingRef.current = true;
    const firstWeek = weeks[0]!;
    const added = Array.from({ length: EXTEND_WEEKS }, (_, i) => addWeeks(firstWeek, -(EXTEND_WEEKS - i)));
    let next = [...added, ...weeks];
    if (next.length > MAX_WEEKS) next = next.slice(0, MAX_WEEKS); // 미래쪽(화면 밖) 끝을 자른다 — 보정 불필요.
    pendingScrollDeltaRef.current += added.length * rowHRef.current;
    setWeeks(next);
    onNeedRange(added[0]!, firstWeek);
  }

  function extendFuture() {
    if (extendingRef.current) return;
    extendingRef.current = true;
    const lastWeek = weeks[weeks.length - 1]!;
    const added = Array.from({ length: EXTEND_WEEKS }, (_, i) => addWeeks(lastWeek, i + 1));
    let next = [...weeks, ...added];
    if (next.length > MAX_WEEKS) {
      const trimCount = next.length - MAX_WEEKS;
      next = next.slice(trimCount); // 과거쪽(화면 위) 끝을 자른다 — 그만큼 scrollTop 도 줄여야 안 튄다.
      pendingScrollDeltaRef.current -= trimCount * rowHRef.current;
    }
    setWeeks(next);
    onNeedRange(addWeeks(lastWeek, 1), weekEnd(added[added.length - 1]!));
  }

  // 2026-08-24 — "헤더 달 기준이 맨 윗줄"이라는 리포트. 화면 최상단 행 하나가 아니라, 지금
  // 뷰포트 안에 걸린 모든 주 행을 보고 "실제로 화면에서 차지하는 픽셀이 가장 큰 달"을 고른다
  // (구글 캘린더식) — 예: 위쪽에 1주만 살짝 걸리고 나머지 5주가 다음 달이면 다음 달을 보여준다.
  // onScroll 과 scrollToDate(즉시 점프, 아래 참조) 가 이 계산을 공유한다 — scrollToDate 가
  // 자기 몫만 맨 위 주 하나로 대충 계산하면(2026-08-25 한때 그렇게 했었다) 그 주가 이전 달
  // 날짜로 더 채워진 주(예: 월 1일이 토요일)일 때 헤더가 "그 전 달"로 잘못 나왔다.
  function notifyVisibleMonth(scrollTop: number, clientHeight: number, weeksArr: Date[]) {
    if (!onVisibleMonthChange) return;
    const startIdx = Math.max(0, Math.floor(scrollTop / rowHRef.current));
    const endIdx = Math.min(weeksArr.length - 1, Math.floor((scrollTop + clientHeight - 1) / rowHRef.current));
    const pxByMonth = new Map<string, { year: number; month: number; px: number }>();
    for (let idx = startIdx; idx <= endIdx; idx++) {
      const w = weeksArr[idx];
      if (!w) continue;
      const rowTop = idx * rowHRef.current;
      const visiblePx = Math.min(rowTop + rowHRef.current, scrollTop + clientHeight) - Math.max(rowTop, scrollTop);
      if (visiblePx <= 0) continue;
      const { year, month } = dominantMonthOfWeek(w);
      const key = `${year}-${month}`;
      const entry = pxByMonth.get(key);
      if (entry) entry.px += visiblePx; else pxByMonth.set(key, { year, month, px: visiblePx });
    }
    let winner: { year: number; month: number; px: number } | null = null;
    for (const entry of pxByMonth.values()) {
      if (!winner || entry.px > winner.px) winner = entry;
    }
    if (winner) {
      const key = `${winner.year}-${winner.month}`;
      if (key !== lastVisibleMonthRef.current) {
        lastVisibleMonthRef.current = key;
        onVisibleMonthChange(winner.year, winner.month);
      }
    }
  }

  const rafPendingRef = useRef(false);
  function onScroll() {
    if (rafPendingRef.current) return;
    rafPendingRef.current = true;
    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      const el = containerRef.current;
      if (!el) return;
      const { scrollTop, scrollHeight, clientHeight } = el;

      // 우리가 방금 보정 effect 에서 쓴 값과 (거의) 같으면 그 메아리 — extendPast/Future
      // 재판정 없이 건너뛴다(위 lastSelfScrollTopRef 주석 참조). 그 사이 실제로 값이 또
      // 바뀌었으면(사용자가 더 스크롤) 값이 달라져 있으니 아래로 그대로 통과한다.
      // 완전 일치(===)가 아니라 1px 허용 오차를 두는 이유 — 브라우저 배율(디스플레이 스케일링·
      // 페이지 줌)이 100% 가 아니면 우리가 쓴 값과 el.scrollTop 이 읽어 주는 값이 서브픽셀
      // 반올림으로 미세하게 어긋날 수 있다. 그러면 이 비교가 항상 실패해 메아리를 못 걸러내고
      // 스크롤할 때마다 미세하게 튀는 게 남는다.
      if (lastSelfScrollTopRef.current != null) {
        const isEcho = Math.abs(scrollTop - lastSelfScrollTopRef.current) < 1;
        lastSelfScrollTopRef.current = null;
        if (isEcho) return;
      }

      // 위치만 보지 않고 방향도 본다(위 prevScrollTopRef 주석 참조) — 실제로 아래로 스크롤
      // 중이면 extendPast 를, 위로 스크롤 중이면 extendFuture 를 부르지 않는다. 처음 평가라
      // 방향을 모르면(prevScrollTop == null) 기존처럼 위치만으로 판단.
      const prevScrollTop = prevScrollTopRef.current;
      prevScrollTopRef.current = scrollTop;
      const scrollingDown = prevScrollTop != null && scrollTop > prevScrollTop;
      const scrollingUp = prevScrollTop != null && scrollTop < prevScrollTop;

      if (scrollTop < EDGE_PREFETCH_PX && !scrollingDown) extendPast();
      if (scrollHeight - scrollTop - clientHeight < EDGE_PREFETCH_PX && !scrollingUp) extendFuture();

      notifyVisibleMonth(scrollTop, clientHeight, weeks);
    });
  }

  /** 임의의 날짜로 점프 — 이미 마운트된 주면 그 위치로, 아니면 그 날짜를 중심으로 창을 다시 세운다. */
  function scrollToDate(date: Date) {
    const target = weekStartOf(date);
    const idx = weeks.findIndex((w) => sameWeek(w, target));
    /*
     * ⭐ 2026-10-30 「다음 달 버튼이 제대로 안 된다」(사용자 리포트 · 재현함).
     *   목표 주가 창 «안»에 있어도 그 아래 남은 줄이 화면 높이보다 적으면 `scrollTop` 이 브라우저
     *   최대값에 «잘린다» — 10월 → 다음 달: 11/1 주는 창의 12번째 줄인데 아래가 모자라 10/4 주에서
     *   멈췄다. 제목은 「11월」인데 격자는 10월이 대부분이었다. 그 대입은 메아리로 표시돼
     *   `extendFuture` 도 안 불린다 ⇒ 저절로 풀리지 않는다.
     *   ⇒ 그 경우는 «창 밖»과 같이 목표를 중심으로 창을 다시 세운다(아래 분기).
     */
    const elNow = containerRef.current;
    const fits = !elNow || idx * rowHRef.current <= weeks.length * rowHRef.current - elNow.clientHeight;
    if (idx !== -1 && fits) {
      const el = containerRef.current;
      if (!el) return;
      // 2026-08-25 — "헤더로 이동하면 그 달 첫 주가 아니라 마지막 주가 보인다" 리포트.
      // 예전엔 scrollTo({behavior:'smooth'}) 로 애니메이션 스크롤했는데, 애니메이션이
      // EDGE_PREFETCH_PX 문턱을 지나가는 중에 onScroll 이 extendPast/extendFuture 를
      // 발동시켰다 — 그 보정(el.scrollTop 직접 대입)이 브라우저의 진행 중이던 smooth
      // 스크롤 애니메이션을 즉시 중단시켜, 원래 목표(그 달 첫 주)가 아니라 보정이 멈춘
      // 아무 위치에 landing 했다(재현: 이미 로드된 범위 안의 달로 점프할 때만 — 안 로드된
      // 먼 달은 아래 else 분기(즉시 점프)라 안 겪는다). 위 [weeks] 보정 effect 와 완전히
      // 같은 방식(즉시 대입 + echo 표시 + 방향 추적 리셋)으로 바꿔 애니메이션 자체를 없앴다.
      el.scrollTop = idx * rowHRef.current;
      lastSelfScrollTopRef.current = el.scrollTop;
      prevScrollTopRef.current = null;
      // 위 echo 표시 때문에 onScroll 이 이번 스크롤에서는 onVisibleMonthChange 재계산을
      // 건너뛴다(정상 — extend 오발동 방지가 목적) — 그래서 여기서 직접 동기화해 둔다.
      // 2026-08-25 — 맨 위 주(=점프 목표가 속한 주) 하나만 보고 판단하면 월 1일이 토요일
      // 처럼 그 주의 대부분이 전달 날짜일 때 헤더가 "그 전 달"로 잘못 나왔다(픽셀 비중
      // 계산으로 한 번 고쳤었는데, 더 단순하게 "그다음 주(2번째 줄)"를 대신 보는 쪽으로
      // 정정 — 사용자 요청). 그 주는 경계에 걸릴 일이 거의 없어(달의 두 번째 주는 온전히
      // 그 달 날짜뿐) 안정적으로 목표 달을 가리킨다. 맨 끝(2번째 줄이 없음)이면 첫 줄로 폴백.
      const secondRow = weeks[idx + 1] ?? weeks[idx]!;
      const dom = dominantMonthOfWeek(secondRow);
      lastVisibleMonthRef.current = `${dom.year}-${dom.month}`;
      onVisibleMonthChange?.(dom.year, dom.month);
      return;
    }
    // 다시 세운 창도 목표 아래로 «화면 한 장» 이상은 있어야 같은 잘림이 안 난다(키 큰 화면).
    const futureWeeks = elNow ? Math.max(INITIAL_FUTURE_WEEKS, Math.ceil(elNow.clientHeight / rowHRef.current)) : INITIAL_FUTURE_WEEKS;
    const next = buildWindow(target, futureWeeks);
    pendingScrollTopRef.current = INITIAL_PAST_WEEKS * rowHRef.current;
    setWeeks(next);
    onNeedRange(next[0]!, weekEnd(next[next.length - 1]!));
    // 위와 동일한 이유로 목표 주(next[INITIAL_PAST_WEEKS]) 대신 그다음 주를 본다.
    const secondRow = next[INITIAL_PAST_WEEKS + 1] ?? next[INITIAL_PAST_WEEKS]!;
    const dom = dominantMonthOfWeek(secondRow);
    lastVisibleMonthRef.current = `${dom.year}-${dom.month}`;
    onVisibleMonthChange?.(dom.year, dom.month);
  }

  /*
   * ⭐ 2026-10-19 — 주 칸 높이가 바뀌면(달력 영역이 넉넉해져 4줄 → 5줄 등) 보던 주를 붙잡는다.
   *   scrollTop 을 «몇 번째 주 + 그 안의 비율»로 읽어 새 높이로 다시 놓는다. 스스로 옮긴 것이라 메아리로 표시한다.
   */
  useLayoutEffect(() => {
    const prev = rowHRef.current;
    if (prev === rowH) return;
    rowHRef.current = rowH;
    const el = containerRef.current;
    const rows = anchorRowsRef.current;
    anchorRowsRef.current = null;
    if (!el) return;
    el.scrollTop = (rows ?? el.scrollTop / prev) * rowH;
    lastSelfScrollTopRef.current = el.scrollTop;
    prevScrollTopRef.current = null;
  }, [rowH]); // eslint-disable-line react-hooks/exhaustive-deps

  return { weeks, onScroll, scrollToDate };
}
